import { useState, useEffect, useMemo } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import Logo from '../components/Logo'
import { memoryApi, type MemoryLeaderboardEntry, type MemoryInfo } from '../utils/api'

function QuantumMemoryLeaderboardPage() {
  const { memoryId } = useParams<{ memoryId: string }>()
  const [searchParams] = useSearchParams()
  const [info, setInfo] = useState<MemoryInfo | null>(null)
  const [leaderboard, setLeaderboard] = useState<MemoryLeaderboardEntry[]>([])
  const [loading, setLoading] = useState(true)
  const view = searchParams.get('view') || 'participants'  // 'participants' or 'hardware'

  useEffect(() => {
    if (!memoryId) return
    memoryApi.getInfo(memoryId).then(setInfo).catch(() => {})
    setLoading(true)
    memoryApi.getLeaderboard(memoryId, 'hardware')
      .then(r => setLeaderboard(r.leaderboard))
      .catch(() => setLeaderboard([]))
      .finally(() => setLoading(false))
  }, [memoryId])

  // Hardware ranking: group by backend, compute average LER
  const hardwareRanking = useMemo(() => {
    const byBackend: Record<string, { lers: number[]; count: number }> = {}
    for (const entry of leaderboard) {
      if (entry.logical_error_rate == null || entry.backend_name == null) continue
      if (!byBackend[entry.backend_name]) {
        byBackend[entry.backend_name] = { lers: [], count: 0 }
      }
      byBackend[entry.backend_name].lers.push(entry.logical_error_rate)
      byBackend[entry.backend_name].count++
    }
    return Object.entries(byBackend)
      .map(([backend, data]) => ({
        backend,
        avgLer: data.lers.reduce((a, b) => a + b, 0) / data.lers.length,
        bestLer: Math.min(...data.lers),
        submissions: data.count,
      }))
      .sort((a, b) => a.avgLer - b.avgLer)
  }, [leaderboard])

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-cyan-50">
      <header className="bg-white/80 backdrop-blur border-b border-emerald-200 px-6 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2">
            <Logo size="small" />
          </Link>
          <span className="text-qcloud-muted">/</span>
          <Link to={`/memory/${memoryId}`} className="text-sm text-emerald-600 hover:text-emerald-700">Challenge</Link>
          <span className="text-qcloud-muted">/</span>
          <span className="text-sm font-semibold">Leaderboard</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to={`/memory/${memoryId}/queue`} className="text-sm text-gray-500 hover:text-emerald-600">Queue</Link>
          <Link to={`/memory/${memoryId}`} className="text-sm text-gray-500 hover:text-emerald-600">Editor</Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-1">
          {info?.title || 'QEC Challenge'} — Leaderboard
        </h1>
        <p className="text-gray-500 mb-6">Ranked by lowest logical error rate (lower is better)</p>

        {/* View toggle */}
        <div className="flex gap-2 mb-6">
          <Link to={`/memory/${memoryId}/leaderboard`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              view === 'participants' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300'
            }`}>
            Participant Ranking
          </Link>
          <Link to={`/memory/${memoryId}/leaderboard?view=hardware`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              view === 'hardware' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-purple-300'
            }`}>
            Hardware Ranking
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : view === 'hardware' ? (
          /* Hardware Ranking — grouped by backend, sorted by avg LER */
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {hardwareRanking.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <p className="text-lg">No hardware results yet</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <th className="px-4 py-3 w-16">Rank</th>
                    <th className="px-4 py-3">Backend</th>
                    <th className="px-4 py-3 text-right">Avg LER</th>
                    <th className="px-4 py-3 text-right">Best LER</th>
                    <th className="px-4 py-3 text-right">Submissions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {hardwareRanking.map((hw, i) => (
                    <tr key={hw.backend} className="hover:bg-gray-50/50">
                      <td className="px-4 py-4">
                        <span className={`font-bold text-lg ${
                          i === 0 ? 'text-yellow-500' : i === 1 ? 'text-slate-400' : i === 2 ? 'text-orange-400' : 'text-gray-400'
                        }`}>
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm font-semibold">{hw.backend}</span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className={`font-mono font-bold ${
                          hw.avgLer < 0.01 ? 'text-blue-600' : hw.avgLer < 0.1 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {(hw.avgLer * 100).toFixed(3)}%
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="font-mono text-green-600">{(hw.bestLer * 100).toFixed(3)}%</span>
                      </td>
                      <td className="px-4 py-4 text-right text-sm text-gray-600">{hw.submissions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          /* Participant Ranking */
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {leaderboard.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <div className="text-4xl mb-3">🏆</div>
                <p className="text-lg">No submissions yet</p>
                <p className="text-sm mt-1">Be the first to submit!</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <th className="px-4 py-3 w-16">Rank</th>
                    <th className="px-4 py-3">Participant</th>
                    <th className="px-4 py-3">Affiliation</th>
                    <th className="px-4 py-3 text-right">Logical Error Rate</th>
                    <th className="px-4 py-3 text-right">QEC Rounds</th>
                    <th className="px-4 py-3 text-right">Shots</th>
                    <th className="px-4 py-3 text-right">Backend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leaderboard.map((entry, index) => (
                    <tr key={entry.student_label + '-' + index} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3">
                        <span className={`font-bold ${
                          index === 0 ? 'text-yellow-500 text-lg' :
                          index === 1 ? 'text-slate-400 text-lg' :
                          index === 2 ? 'text-orange-400 text-lg' :
                          'text-gray-400'
                        }`}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{entry.display_name || entry.student_label}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{entry.affiliation || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-mono font-bold ${
                          entry.logical_error_rate === 0 ? 'text-emerald-600' :
                          entry.logical_error_rate < 0.01 ? 'text-blue-600' :
                          entry.logical_error_rate < 0.1 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {entry.logical_error_rate != null
                            ? entry.logical_error_rate === 0
                              ? '0.000%'
                              : (entry.logical_error_rate * 100).toFixed(3) + '%'
                            : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">{entry.qec_rounds ?? '—'}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">
                        {entry.shots ? entry.shots.toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                          {entry.backend_name || '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default QuantumMemoryLeaderboardPage
